/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    alpha,
    Tooltip,
    IconButton,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Checkbox,
    Divider,
    Badge,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { dispatch, useSelector as useReduxSelector } from 'src/redux/store';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import SourceLeadPopup from './SourceLeadPopup';

// Column configuration matching TreatmentStatusSummaryTable style
const COLUMN_CONFIG = {
    leads: { label: 'Leads', color: '#3b82f6' },
    revenue: { label: 'Revenue', color: '#10b981' },
    conversionRate: { label: 'Conversion Rate', color: '#8b5cf6' },
};

const TABLE_NAME = 'Treatment Table';

const ALL_COLUMNS = [
    { field: 'name', label: 'Treatment' },
    { field: 'leads', label: 'Leads' },
    { field: 'revenue', label: 'Revenue' },
    { field: 'conversionRate', label: 'Conversion Rate' },
];

export default function TreatmentTable({ treatments = [], title, onTreatmentClick, practiceId, dateRange }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [sortModel, setSortModel] = useState([{ field: 'revenue', sort: 'desc' }]);
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    // Popup state
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupConfig, setPopupConfig] = useState({
        treatment: null,
        title: '',
    });

    const handleLeadsClick = useCallback((treatment) => {
        const value = treatment.leads || treatment.count || 0;
        if (!value || value <= 0 || !practiceId) return;
        setPopupConfig({
            treatment: treatment.id,
            title: `${treatment.name} — All Leads`,
        });
        setPopupOpen(true);
    }, [practiceId]);

    const handleClosePopup = useCallback(() => {
        setPopupOpen(false);
    }, []);

    // Table preferences from Redux
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    // Load user preferences on mount
    useEffect(() => {
        if (userId) {
            dispatch(getTablePreferences({ practiceUserId: userId }));
        }
    }, [userId]);

    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `£${(value / 1000000).toFixed(1)}M`;
        }
        if (value >= 1000) {
            return `£${(value / 1000).toFixed(1)}K`;
        }
        return `£${value?.toFixed(0) || 0}`;
    };

    // Process data to ensure consistent structure
    const processedData = useMemo(() =>
        treatments.map((treatment, index) => ({
            id: treatment.id || index,
            name: treatment.name,
            leads: treatment.leads || treatment.count || 0,
            revenue: treatment.revenue || 0,
            conversionRate: treatment.conversionRate || 0,
            color: treatment.color || '#6366f1',
        })),
        [treatments]
    );

    // Filter data based on search
    const filteredData = useMemo(() => {
        if (!searchQuery) return processedData;
        return processedData.filter((row) =>
            row.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [processedData, searchQuery]);

    // Save preferences to API
    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        dispatch(setTablePreferenceLoading(true));
        const tData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tData) && tData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    // Load saved preferences
    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId]);

    // Column visibility model for DataGrid
    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    const isFilterActive = hiddenColumns.length > 0;

    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    // Export CSV — exports exactly what the user currently sees (filtered view)
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!filteredData || filteredData.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = visibleCols.map((c) => c.label);
        const rows = filteredData.map((row) => visibleCols.map((col) => {
            if (col.field === 'revenue') return formatCurrency(row.revenue || 0);
            if (col.field === 'conversionRate') return `${row.conversionRate || 0}%`;
            return row[col.field] ?? '-';
        }));
        const csvContent = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('treatments', dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, hiddenColumns]);

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Treatment',
            flex: 1.5,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                    <Box
                        sx={{
                            width: 4,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: params.row.color,
                        }}
                    />
                    <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'leads',
            headerName: 'Leads',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.leads;
                return (
                    <Chip
                        label={value.toLocaleString()}
                        size="small"
                        onClick={value > 0 && practiceId ? (e) => { e.stopPropagation(); handleLeadsClick(params.row); } : undefined}
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 40,
                            cursor: value > 0 && practiceId ? 'pointer' : 'default',
                            '&:hover': value > 0 && practiceId ? { bgcolor: alpha(config.color, 0.22) } : {},
                        }}
                    />
                );
            },
        },
        {
            field: 'revenue',
            headerName: 'Revenue',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.revenue;
                return (
                    <Chip
                        label={formatCurrency(value)}
                        size="small"
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                        }}
                    />
                );
            },
        },
        {
            field: 'conversionRate',
            headerName: 'Conversion Rate',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const value = params.value || 0;
                const config = COLUMN_CONFIG.conversionRate;
                return (
                    <Chip
                        label={`${value}%`}
                        size="small"
                        sx={{
                            bgcolor: value > 0 ? alpha(config.color, 0.12) : 'transparent',
                            color: value > 0 ? config.color : 'text.disabled',
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 26,
                            minWidth: 50,
                        }}
                    />
                );
            },
        },
    ], [practiceId, handleLeadsClick]);

    const handleRowClick = (params) => {
        if (onTreatmentClick) {
            onTreatmentClick(params.row);
        }
    };

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                mb: 3,
            }}
        >
            {/* Header with Search and Toolbar */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Tooltip title="Breakdown of leads by treatment type with enquiry volume and revenue." placement="bottom" arrow
                    slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                            {title}
                        </Typography>
                        <Iconify icon="mdi:information-outline" width={14} sx={{ color: '#94a3b8' }} />
                    </Box>
                </Tooltip>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search treatment..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {/* Column visibility */}
                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {/* Export */}
                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Column visibility popover */}
                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox
                                            size="small"
                                            checked={isVisible}
                                            sx={{ p: 0, mr: 1 }}
                                        />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    {/* Export popover */}
                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            {/* DataGrid */}
            <Box>
                <DataGridPro
                    rows={filteredData}
                    columns={columns}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    onRowClick={handleRowClick}
                    autoHeight
                    disableRowSelectionOnClick
                    disableColumnMenu={false}
                    pagination
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    columnVisibilityModel={columnVisibilityModel}
                    sx={{
                        border: 'none',
                        cursor: onTreatmentClick ? 'pointer' : 'default',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f1f5f9',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc',
                        },
                    }}
                />
            </Box>

            {treatments.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No treatment data available
                    </Typography>
                </Box>
            )}

            {/* Lead detail popup */}
            <SourceLeadPopup
                open={popupOpen}
                onClose={handleClosePopup}
                practiceId={practiceId}
                status="All"
                statusLabel="All Leads"
                dateRange={dateRange}
                title={popupConfig.title}
                treatment={popupConfig.treatment}
            />
        </Box>
    );
}

TreatmentTable.propTypes = {
    treatments: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string.isRequired,
            leads: PropTypes.number,
            count: PropTypes.number,
            revenue: PropTypes.number,
            conversionRate: PropTypes.number,
            color: PropTypes.string,
        })
    ).isRequired,
    title: PropTypes.string,
    onTreatmentClick: PropTypes.func,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dateRange: PropTypes.shape({
        startDate: PropTypes.instanceOf(Date),
        endDate: PropTypes.instanceOf(Date),
    }),
};

TreatmentTable.defaultProps = {
    title: 'All Treatments',
    onTreatmentClick: null,
    practiceId: null,
    dateRange: null,
};
